import React from 'react';

// ReleaseFilterBar — shared header for every analytical admin page so the
// user can scope what they're looking at by app version, time window,
// and telemetry layer (production vs developer baseline). State lives in
// useReleaseFilter (../hooks/useReleaseFilter.js), backed by the URL
// query string so links are shareable.
//
// Usage:
//   const filter = useReleaseFilter();
//   <ReleaseFilterBar filter={filter} appVersions={kpiArray} />
//   ...
//   axios.get('/admin/api/...', { params: filter.queryParams });
//
// appVersions (optional): array of {app_version_name, session_count,
// unique_devices, last_seen} returned by an endpoint that aggregates
// builds-seen for the current window. Drives the chip strip below the
// filter row. Pass null/undefined to hide the strip.

const OPS = [
    { id: 'all',         label: 'All builds' },
    { id: 'current',     label: 'Current build only' },
    { id: 'pre_current', label: 'Pre-current only' },
    { id: 'eq',          label: 'Specific build' },
    { id: 'ne',          label: 'Exclude build' },
];

const DAYS = [
    { id: 1,   label: 'Last 24h' },
    { id: 7,   label: 'Last 7d' },
    { id: 30,  label: 'Last 30d' },
    { id: 90,  label: 'Last 90d' },
    { id: 365, label: 'Last 365d' },
];

const LAYERS = [
    { id: 'all',                 label: 'All' },
    { id: 'production',          label: 'Production' },
    { id: 'developer_baseline',  label: 'Dev cohort' },
];

export default function ReleaseFilterBar({ filter, appVersions, currentVersion }) {
    const { filter: f, setFilter, days, setDays, layer, setLayer } = filter;
    const op    = f.op;
    const value = f.value;
    const showSpecific = op === 'eq' || op === 'ne' || op === 'in';

    const allVersions = (appVersions || []).map(v => v.app_version_name).filter(Boolean);

    return (
        <div className="bg-white rounded-lg shadow-md p-4 mb-4 text-sm">
            {/* Row 1 — release op */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-xs uppercase text-gray-500 mr-1">Release</span>
                {OPS.map(o => (
                    <Chip
                        key={o.id}
                        active={op === o.id}
                        onClick={() => setFilter({ op: o.id, value: showSpecific && o.id !== op ? '' : value })}
                        highlight={o.id === 'current'}>
                        {o.label}
                    </Chip>
                ))}
                {showSpecific && (
                    <select
                        value={value}
                        onChange={e => setFilter({ value: e.target.value })}
                        className="ml-2 border rounded px-2 py-1 text-xs">
                        <option value="">— pick a version —</option>
                        {allVersions.map(v => (
                            <option key={v} value={v}>{v}</option>
                        ))}
                    </select>
                )}
            </div>

            {/* Row 2 — window + layer */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-xs uppercase text-gray-500 mr-1">Window</span>
                <select
                    value={days}
                    onChange={e => setDays(Number(e.target.value))}
                    className="border rounded px-2 py-1 text-xs">
                    {DAYS.map(d => (
                        <option key={d.id} value={d.id}>{d.label}</option>
                    ))}
                </select>
                <span className="text-xs uppercase text-gray-500 ml-3 mr-1">Layer</span>
                {LAYERS.map(l => (
                    <Chip key={l.id} active={layer === l.id} onClick={() => setLayer(l.id)}>
                        {l.label}
                    </Chip>
                ))}
            </div>

            {/* Row 3 — KPI strip of seen versions */}
            {appVersions && appVersions.length > 0 && (
                <div>
                    <div className="text-xs uppercase text-gray-500 mb-1">
                        Builds seen in this window
                        {currentVersion && (
                            <span className="ml-2 normal-case text-gray-400">
                                · current shipping: <span className="font-mono text-gray-700">{currentVersion}</span>
                            </span>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {appVersions.map(v => {
                            const isCurrent  = currentVersion && v.app_version_name === currentVersion;
                            // A chip is "selected" when the current filter
                            // is op=eq AND value matches this chip — that's
                            // the result of clicking this chip.
                            const isSelected = op === 'eq' && value === v.app_version_name;
                            const cls = isSelected ? 'bg-amber-500 text-white ring-2 ring-amber-300'
                                       : isCurrent ? 'bg-blue-600 text-white hover:bg-blue-700'
                                       : 'bg-gray-200 text-gray-700 hover:bg-gray-300';
                            const versionName = v.app_version_name || '?';
                            return (
                                <span
                                    key={versionName}
                                    onClick={() => {
                                        // Toggle: clicking the already-selected
                                        // chip clears the filter.
                                        if (isSelected) {
                                            setFilter({ op: 'all', value: '' });
                                        } else if (versionName !== '?') {
                                            setFilter({ op: 'eq', value: versionName });
                                        }
                                    }}
                                    className={`px-2 py-0.5 rounded text-xs font-mono cursor-pointer transition ${cls}`}
                                    title={isSelected
                                        ? `Click to clear filter (currently showing only ${versionName})`
                                        : `Click to filter to ${versionName} only · last seen ${v.last_seen}`}>
                                    {versionName}
                                    <span className="ml-1 opacity-70">
                                        · {v.session_count} sess
                                        {v.unique_devices != null ? ` · ${v.unique_devices} dev` : ''}
                                    </span>
                                    {isCurrent && !isSelected && <span className="ml-1">●</span>}
                                    {isSelected && <span className="ml-1">✓</span>}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function Chip({ active, onClick, children, highlight }) {
    const base = 'px-3 py-1 rounded-full text-xs font-medium transition cursor-pointer ';
    const cls = active
        ? (highlight ? 'bg-blue-600 text-white' : 'bg-gray-700 text-white')
        : (highlight ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200');
    return <span className={base + cls} onClick={onClick}>{children}</span>;
}
